import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  PasswordInput,
  Button,
  Alert,
  Anchor,
  Loader,
  Center,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconLock } from '@tabler/icons-react';
import { useAuthService } from '@actschurch/ccms-sdk';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authService = useAuthService();

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token || !email) {
      setValidating(false);
      setTokenValid(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ok = await authService.validateResetToken(token, email);
        if (!cancelled) {
          setTokenValid(ok);
        }
      } catch {
        if (!cancelled) setTokenValid(false);
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, email, authService]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      await authService.resetPassword({
        email,
        password,
        confirmPassword,
        code: token,
      });
      setDone(true);
      setTimeout(() => navigate('/auth/login', { replace: true }), 2500);
    } catch (err: any) {
      setError(err?.message || 'Could not reset password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container size={420} my={60}>
      <Title order={2} ta="center" mb="md">
        Choose a new password
      </Title>

      <Paper withBorder shadow="sm" p="lg" radius="md">
        {validating ? (
          <Center py="lg"><Loader /></Center>
        ) : !tokenValid ? (
          <Stack>
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              This reset link is invalid or has expired.
            </Alert>
            <Anchor component={Link} to="/auth/forgot-password" ta="center">
              Request a new link
            </Anchor>
          </Stack>
        ) : done ? (
          <Alert icon={<IconCheck size={16} />} color="green" variant="light">
            Password updated. Redirecting to sign-in…
          </Alert>
        ) : (
          <form onSubmit={onSubmit}>
            <Stack gap="md">
              {error && (
                <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                  {error}
                </Alert>
              )}
              <Text size="sm" c="dimmed">
                Setting a new password for <strong>{email}</strong>.
              </Text>
              <PasswordInput
                label="New password"
                required
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                autoComplete="new-password"
              />
              <PasswordInput
                label="Confirm new password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                autoComplete="new-password"
              />
              <Button type="submit" loading={submitting} leftSection={<IconLock size={16} />} fullWidth>
                Update password
              </Button>
            </Stack>
          </form>
        )}
      </Paper>
    </Container>
  );
}
